import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Send, CheckCircle, XCircle, BookOpen, Plus } from 'lucide-react'
import { DashboardStats } from '@/types'
import { useAuthStore } from '@/hooks/useAuth'

export default function Dashboard() {
  const { user } = useAuthStore()
  const [stats, setStats] = useState<DashboardStats>({
    totalWorks: 0,
    activeSubmissions: 0,
    acceptances: 0,
    rejections: 0,
    openReadingPeriods: 0,
  })

  useEffect(() => {
    // TODO: Fetch real data from API
    setStats({
      totalWorks: 12,
      activeSubmissions: 5,
      acceptances: 3,
      rejections: 8,
      openReadingPeriods: 15,
    })
  }, [])

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.firstName || 'Writer'}!
        </h1>
        <p className="mt-2 text-gray-600">
          Here's an overview of your literary submissions
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.totalWorks}</h3>
              <p className="text-gray-600">Total Works</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <Send className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.activeSubmissions}</h3>
              <p className="text-gray-600">Active Submissions</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.acceptances}</h3>
              <p className="text-gray-600">Acceptances</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.rejections}</h3>
              <p className="text-gray-600">Rejections</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <BookOpen className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{stats.openReadingPeriods}</h3>
              <p className="text-gray-600">Open Periods</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/works/new"
              className="flex items-center p-3 rounded-md bg-primary-50 hover:bg-primary-100 transition-colors"
            >
              <Plus className="h-5 w-5 text-primary-600 mr-3" />
              <span className="text-primary-700 font-medium">Create New Work</span>
            </Link>
            <Link
              to="/journals"
              className="flex items-center p-3 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
            >
              <BookOpen className="h-5 w-5 text-purple-600 mr-3" />
              <span className="text-purple-700 font-medium">Browse Journals</span>
            </Link>
            <Link
              to="/submissions"
              className="flex items-center p-3 rounded-md bg-yellow-50 hover:bg-yellow-100 transition-colors"
            >
              <Send className="h-5 w-5 text-yellow-600 mr-3" />
              <span className="text-yellow-700 font-medium">Track Submissions</span>
            </Link>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span>Acceptance from Literary Review</span>
              <span className="ml-auto text-gray-500">2 days ago</span>
            </div>
            <div className="flex items-center text-sm">
              <Send className="h-4 w-4 text-blue-500 mr-2" />
              <span>Submitted "Ocean Waves" to Poetry Today</span>
              <span className="ml-auto text-gray-500">1 week ago</span>
            </div>
            <div className="flex items-center text-sm">
              <FileText className="h-4 w-4 text-purple-500 mr-2" />
              <span>Created new work "Mountain Song"</span>
              <span className="ml-auto text-gray-500">2 weeks ago</span>
            </div>
          </div>
        </div>
      </div>

      {/* Open Reading Periods */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Open Reading Periods (Next 30 Days)
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Journal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deadline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Accepts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">Poetry Magazine</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  Dec 31, 2024
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  Poetry, Short Fiction
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to="/journals"
                    className="text-primary-600 hover:text-primary-900 font-medium"
                  >
                    View Details
                  </Link>
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">Literary Quarterly</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  Jan 15, 2025
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  Essays, Short Stories
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to="/journals"
                    className="text-primary-600 hover:text-primary-900 font-medium"
                  >
                    View Details
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}